import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiDelete, apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useAppDialogs } from "@/components/ui/app-dialogs";

type RoleTemplate = {
  id: string;
  tenantId: string;
  name: string;
  baseRole: "owner" | "staff";
  jobTitle: string | null;
  operationalRoleId?: string | null;
  operationalScope?: string | null;
  isSystemPreset?: boolean;
  permissions?: string[];
};

type TenantOption = { id: string; name: string };

const PROFILE_LABELS: Record<string, string> = {
  fast_food: "Fast food",
  restaurant: "Restaurante",
  market: "Almacén / Mercado",
  pharmacy: "Farmacia",
  fashion: "Indumentaria",
  bazar: "Bazar",
  catalog: "Catálogo",
  dietetics: "Dietética",
  digital_products: "Productos digitales",
  pet: "Pet shop",
  cafe: "Cafetería / Panadería",
};

export function AdminRolesPanel({ tenants }: { tenants: TenantOption[] }) {
  const [rolesTenantId, setRolesTenantId] = useState("");
  const { confirm } = useAppDialogs();
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [businessProfile, setBusinessProfile] = useState("catalog");
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleBase, setNewRoleBase] = useState<"owner" | "staff">("staff");
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editBase, setEditBase] = useState<"owner" | "staff">("staff");

  useEffect(() => {
    if (!rolesTenantId && tenants[0]) setRolesTenantId(tenants[0].id);
  }, [tenants, rolesTenantId]);

  const load = useCallback(async () => {
    if (!rolesTenantId) return;
    setLoading(true);
    try {
      const res = await apiGetJson<{
        templates: RoleTemplate[];
        businessProfile?: string;
      }>(`/api/admin/role-templates?tenantId=${encodeURIComponent(rolesTenantId)}`);
      setTemplates(res.templates);
      if (res.businessProfile) setBusinessProfile(res.businessProfile);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar roles");
    } finally {
      setLoading(false);
    }
  }, [rolesTenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const provisionFromProfile = async () => {
    if (!rolesTenantId) return;
    setProvisioning(true);
    try {
      const res = await apiSendJson<{
        profile: string;
        created: number;
        updated: number;
        templates: RoleTemplate[];
      }>("/api/admin/role-templates", "POST", {
        action: "provision",
        tenantId: rolesTenantId,
        businessProfile,
      });
      setTemplates(res.templates);
      toast.success(
        `Roles operativos (${PROFILE_LABELS[res.profile] ?? res.profile}): ${res.created} nuevos, ${res.updated} actualizados`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al provisionar");
    } finally {
      setProvisioning(false);
    }
  };

  const createTemplate = async () => {
    if (!rolesTenantId || !newRoleName.trim()) return;
    try {
      await apiSendJson("/api/admin/role-templates", "POST", {
        tenantId: rolesTenantId,
        name: newRoleName.trim(),
        baseRole: newRoleBase,
      });
      setNewRoleName("");
      toast.success("Plantilla personalizada creada");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const saveEdit = async (tpl: RoleTemplate) => {
    try {
      await apiSendJson("/api/admin/role-templates", "POST", {
        id: tpl.id,
        tenantId: tpl.tenantId,
        name: editName.trim() || tpl.name,
        baseRole: editBase,
        jobTitle: tpl.jobTitle,
      });
      setEditId(null);
      toast.success("Guardado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const removeTemplate = async (tpl: RoleTemplate) => {
    if (tpl.isSystemPreset) {
      toast.error("Los roles del preset operativo no se eliminan desde acá. Podés editar el nombre visible.");
      return;
    }
    if (!(await confirm({ title: "¿Eliminar esta plantilla de rol?", destructive: true, confirmLabel: "Eliminar" }))) return;
    try {
      await apiDelete(`/api/admin/role-templates?id=${encodeURIComponent(tpl.id)}`);
      toast.success("Eliminada");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const systemCount = templates.filter((t) => t.isSystemPreset).length;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Plantillas por workspace para invitar al equipo. Los <strong>roles operativos</strong> (cocina, caja,
        mostrador, etc.) vienen del <code>business_profile</code> del tenant y se cargan con el botón de abajo.
        Las plantillas manuales son extras (permisos base <code>owner</code> / <code>staff</code>).
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] max-w-xs">
          <Label className="text-xs text-muted-foreground">Workspace</Label>
          <Select value={rolesTenantId} onValueChange={setRolesTenantId}>
            <SelectTrigger className="mt-1 h-9 bg-secondary/50 text-xs">
              <SelectValue placeholder="Elegí workspace" />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-xs text-muted-foreground">Perfil operativo (FM)</Label>
          <Select value={businessProfile} onValueChange={setBusinessProfile}>
            <SelectTrigger className="mt-1 h-9 bg-secondary/50 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PROFILE_LABELS).map(([k, label]) => (
                <SelectItem key={k} value={k}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          size="sm"
          variant="default"
          className="gap-2"
          disabled={!rolesTenantId || provisioning}
          onClick={() => void provisionFromProfile()}
        >
          {provisioning ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Generar roles del perfil
        </Button>
      </div>
      {systemCount > 0 && (
        <p className="text-xs text-emerald-600 dark:text-emerald-400">
          {systemCount} rol(es) operativo(s) provisionado(s) para este workspace.
        </p>
      )}
      <div className="glass-card flex flex-wrap items-end gap-3 rounded-xl p-4">
        <div className="min-w-[200px] flex-1 space-y-1.5">
          <Label className="text-xs">Plantilla personalizada (opcional)</Label>
          <Input
            className="h-9 bg-secondary/50 text-sm"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            placeholder="Ej. Encargado turno noche"
          />
        </div>
        <div className="w-[140px] space-y-1.5">
          <Label className="text-xs">Permisos base</Label>
          <Select value={newRoleBase} onValueChange={(v) => setNewRoleBase(v as "owner" | "staff")}>
            <SelectTrigger className="h-9 bg-secondary/50 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">staff</SelectItem>
              <SelectItem value="owner">owner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!rolesTenantId || !newRoleName.trim()}
          onClick={() => void createTemplate()}
        >
          Agregar custom
        </Button>
      </div>
      <div className="glass-card rounded-xl">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Cards en mobile */}
            <div className="flex flex-col gap-2 p-3 md:hidden">
              {templates.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  Sin plantillas. Elegí el perfil y pulsá &quot;Generar roles del perfil&quot; (ej. Vibe Studio →
                  fashion: dueño, caja, encargado).
                </p>
              ) : (
                templates.map((r) => (
                  <div key={r.id} className="rounded-lg border border-border p-3">
                    {editId === r.id ? (
                      <div className="space-y-2">
                        <Input
                          className="h-8 bg-secondary/50 text-sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                        />
                        <Select value={editBase} onValueChange={(v) => setEditBase(v as "owner" | "staff")}>
                          <SelectTrigger className="h-8 w-[120px] bg-secondary/50 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="staff">staff</SelectItem>
                            <SelectItem value="owner">owner</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex justify-end gap-1">
                          <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => setEditId(null)}>
                            Cancelar
                          </Button>
                          <Button type="button" size="sm" className="text-xs" onClick={() => void saveEdit(r)}>
                            Guardar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-medium text-sm">{r.name}</span>
                          <Badge variant="outline" className="shrink-0">{r.baseRole}</Badge>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                          {r.operationalRoleId ? (
                            <Badge variant="secondary" className="font-mono text-[10px]">
                              {r.operationalRoleId}
                            </Badge>
                          ) : (
                            <span>Sin rol operativo</span>
                          )}
                          {r.operationalScope ? <span>· {r.operationalScope}</span> : null}
                        </div>
                        <div className="mt-2 flex justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="gap-1 text-xs"
                            onClick={() => {
                              setEditId(r.id);
                              setEditName(r.name);
                              setEditBase(r.baseRole);
                            }}
                          >
                            <Pencil className="h-3 w-3" /> Editar
                          </Button>
                          {!r.isSystemPreset && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="gap-1 text-xs text-destructive"
                              onClick={() => void removeTemplate(r)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Tabla en desktop */}
            <div className="hidden overflow-x-auto md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Rol operativo</TableHead>
                <TableHead>Alcance</TableHead>
                <TableHead>Base</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    Sin plantillas. Elegí el perfil y pulsá &quot;Generar roles del perfil&quot; (ej. Vibe Studio →
                    fashion: dueño, caja, encargado).
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {editId === r.id ? (
                        <Input
                          className="h-8 max-w-xs bg-secondary/50 text-sm"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                        />
                      ) : (
                        <span className="font-medium">{r.name}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.operationalRoleId ? (
                        <Badge variant="secondary" className="font-mono text-[10px]">
                          {r.operationalRoleId}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.operationalScope ?? "—"}
                    </TableCell>
                    <TableCell>
                      {editId === r.id ? (
                        <Select value={editBase} onValueChange={(v) => setEditBase(v as "owner" | "staff")}>
                          <SelectTrigger className="h-8 w-[120px] bg-secondary/50 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="staff">staff</SelectItem>
                            <SelectItem value="owner">owner</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline">{r.baseRole}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editId === r.id ? (
                        <div className="flex justify-end gap-1">
                          <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => setEditId(null)}>
                            Cancelar
                          </Button>
                          <Button type="button" size="sm" className="text-xs" onClick={() => void saveEdit(r)}>
                            Guardar
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="gap-1 text-xs"
                            onClick={() => {
                              setEditId(r.id);
                              setEditName(r.name);
                              setEditBase(r.baseRole);
                            }}
                          >
                            <Pencil className="h-3 w-3" /> Editar
                          </Button>
                          {!r.isSystemPreset && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="gap-1 text-xs text-destructive"
                              onClick={() => void removeTemplate(r)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
